import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { PageContainer, PageHeader } from '../components/layout/Page';
import PasswordChangeForm from '../components/PasswordChangeForm';
import { authErrorMessage } from '../lib/authErrors';
import { formatPersonName } from '../utils/formatName';
import { roleLabel } from '../lib/roles';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 break-words text-sm text-slate-900">{value || '—'}</dd>
    </div>
  );
}

/** "Mi cuenta": account details plus an in-place password change. */
export default function Profile() {
  const { user, userProfile, userRole, changePassword } = useAuth();
  const { organization } = useOrganization();
  const [saved, setSaved] = useState(false);

  const memberships = (userProfile?.user_organization || []).filter((m) => m.is_active && m.organization);
  const organizations = memberships.length
    ? memberships
        .slice()
        .sort((a, b) => Number(b.is_main) - Number(a.is_main))
        .map((m) => m.organization.name)
        .join(', ')
    : organization?.name || '';

  const handlePasswordChange = async (password: string) => {
    setSaved(false);
    const { error } = await changePassword(password);
    if (error) {
      console.error('Password change error:', error);
      return authErrorMessage(error);
    }
    setSaved(true);
    return null;
  };

  return (
    <PageContainer>
      <PageHeader title="Mi cuenta" description="Consulta tus datos de acceso y actualiza tu contraseña." />

      <section aria-labelledby="account-details" className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 id="account-details" className="text-base font-semibold text-slate-900">
          Datos de la cuenta
        </h2>
        <dl className="mt-4 grid gap-5 sm:grid-cols-2">
          <Field label="Nombre" value={formatPersonName(userProfile?.full_name)} />
          <Field label="Correo electrónico" value={userProfile?.email || user?.email || ''} />
          <Field label={memberships.length > 1 ? 'Organizaciones' : 'Organización'} value={organizations} />
          <Field label="Rol" value={roleLabel(userRole)} />
        </dl>
      </section>

      <section aria-labelledby="account-password" className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 id="account-password" className="text-base font-semibold text-slate-900">
          Cambiar contraseña
        </h2>
        <p className="mt-1 text-sm text-slate-600">Usa una contraseña que no utilices en otros sitios.</p>
        <div className="mt-5 max-w-md">
          <PasswordChangeForm onSubmit={handlePasswordChange} submitLabel="Actualizar contraseña" resetOnSuccess />
          {saved && (
            <p role="status" className="mt-4 flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Tu contraseña se actualizó correctamente.
            </p>
          )}
        </div>
      </section>
    </PageContainer>
  );
}
